import {ColumnProps} from "antd/lib/table";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import GeneralNotificationContext from "../../../context/general-notification/context";
import {IGeneralNotification} from "../../../models/general-notification/response";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import Image from "../../general/antd/image";
import eventManager, {EVENT_SUCCESS} from "../../../utils/events";
import {ISort, IStaticFilter} from "../../../models/base/base-query";
import {tableOnChange} from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import {Button, Dropdown} from "antd";
import {MoreOutlined, SendOutlined} from "@ant-design/icons";
import {getNotificationType} from "../../../models/general-notification/enum";
import styles from './styles.module.scss';
import AppContext from "../../../context/app/context";
import Popconfirm from "../../general/antd/pop-confirm";

interface IProps {
}

const GeneralNotificationTable: React.FC<IProps> = (props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {screenSize} = useContext(AppContext);
    const {list, actions, loading, query} = useContext(
        GeneralNotificationContext
    );

    const columns: ColumnProps<IGeneralNotification>[] = [
        {
            title: t("id"),
            dataIndex: "id",
            key: "id",
            sorter: {
                multiple: 1,
            },
            width: 100,
            align: "center",
        },
        {
            title: t("title"),
            dataIndex: "title",
            align: "center",
            key: "name",
            minWidth: 120,
        },
        {
            title: t("image"),
            dataIndex: "image",
            align: "center",
            key: "image",
            width: 80,
            render: (_: any, record) => (
                record?.image?.url ? (
                    <Image
                        src={record.image.url}
                        preview={true}
                        width={50}
                        height={50}
                        style={{objectFit: 'cover', borderRadius: 4}}
                    />
                ) : (
                    <div>
                        -
                    </div>
                )
            ),
        },
        {
            title: t("text"),
            dataIndex: "text",
            align: "center",
            key: "text",
            minWidth: 200,
            render: (_: any, record) => (
                <>
                    <div>
                        {
                            //@ts-ignore
                            record?.text
                        }
                    </div>
                </>
            ),
        },
        {
            title: t("user_slice"),
            dataIndex: "slide",
            align: "center",
            key: "slide",
            render: (_: any, record) => (
                <>
                    <div>
                        {
                            //@ts-ignore
                            getNotificationType(record?.slide)
                        }
                    </div>
                </>
            ),
        },
        {
            title: t("actions"),
            width: 250,
            dataIndex: "",
            align: "center",
            key: "x",
            render: (_: any, record) => (
                screenSize === 'mobileOrTablet' ? (
                    <Dropdown
                        trigger={["click"]}
                        menu={{
                            items: [
                                {
                                    key: 'view',
                                    label: (
                                        <Button
                                            type="link"
                                            onClick={() => navigate(`details/${record?.id}`)}
                                        >
                                            {t("view")}
                                        </Button>
                                    ),
                                },
                                {
                                    key: 'edit',
                                    label: (
                                        <Button
                                            type="link"
                                            onClick={() => navigate(`update/${record?.id}`)}
                                        >
                                            {t("edit")}
                                        </Button>
                                    ),
                                },
                                {
                                    key: 'send',
                                    label: (
                                        <Button
                                            type="link"
                                            loading={loading.includes("notifi")}
                                            onClick={() => actions.sendNotification(record?.id)}
                                        >
                                            {t("send")}
                                        </Button>
                                    ),
                                },
                                {
                                    key: 'delete',
                                    label: (
                                        <Popconfirm
                                            title={t("confirm")}
                                            onConfirm={async () => {
                                                await actions.deleteGeneralNotification(record?.id);
                                                eventManager.emit(EVENT_SUCCESS);
                                            }}
                                        >
                                            <Button type="link" danger>
                                                {t("delete")}
                                            </Button>
                                        </Popconfirm>
                                    ),
                                },
                            ],
                        }}
                    >
                        <Button icon={<MoreOutlined/>}/>
                    </Dropdown>
                ) : (
                    // عرض الأزرار كاملة في الشاشات الكبيرة
                    <Space>
                        <ViewBtn
                            onClick={async () => {
                                navigate(`details/${record?.id}`);
                            }}
                        />
                        <EditBtn
                            onClick={async () => {
                                navigate(`update/${record?.id}`);
                            }}
                        />
                        <Button
                            type="dashed"
                            shape="circle"
                            loading={loading.includes("notifi")}
                            onClick={() => {
                                actions.sendNotification(record?.id);
                            }}
                            icon={<SendOutlined/>}
                        />
                        <DeleteBtn
                            onConfirm={async () => {
                                await actions.deleteGeneralNotification(record?.id);
                                eventManager.emit(EVENT_SUCCESS);
                            }}
                            loading={loading.includes("delete")}
                        />
                    </Space>
                )
            ),
        }
    ];

    // Filters && Sorts
    let tableFiltersProps = {};
    const [sorts, setSorts] = useState<ISort[]>([]);
    const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

    useEffect(() => {
        sorts.length > 0 &&
        actions.setQuery({
            ...query,
            orders: sorts,
        });
    }, [sorts]);

    return (
        <>
            <Table<any>
                rowKey="id"
                showPagination={true}
                columns={columns}
                className={styles.table}
                dataSource={list?.data ?? []}
                loading={loading.includes("list") || list == undefined}
                size="small"
                total={list?.total}
                pageSize={query.perPage}
                page={query.page}
                onPaginationChange={(page, pageSize) => {
                    actions.setQuery({
                        ...query,
                        page,
                        perPage: pageSize,
                    });
                }}
                onChange={(_, tableFilters, tabelSorters) => {
                    tableOnChange(
                        tableFilters,
                        tabelSorters,
                        tableFiltersProps,
                        setSorts,
                        setStaticFilters
                    );
                }}
            />
        </>
    );
};

export default GeneralNotificationTable;
